import { logger } from "@/utils/logger";
export interface ShowroomVehicleItem {
  type?: string; // 'Vehicle Type' in showroom
  fuel?: string; // 'fuel_type' in filtering
  'final price'?: number; // Price field
  [key: string]: any; // Allow other properties
}

export interface ShowroomFilterCriteria {
  vehicleType?: string | string[];
  fuelType?: string | string[];
  minPrice?: number;
  maxPrice?: number;
}

export interface ShowroomVehicleCardByStyleID {
  styleId: string;
  year: number;
  make: string;
  model: string;
  trim: string;
  finalPrice: number;
  drivetrain: string;
  showroomUrlFrench: string;
  showroomUrlEnglish: string;
  buildAndPriceUrlFrench: string;
  buildAndPriceUrlEnglish: string;
  mainPicture: string;
  showroomYoutubeUrlFrench: string[];
  showroomYoutubeUrlEnglish: string[];
  vehicleBrochureFrench: string;
  vehicleBrochureEnglish: string;
}

// In-memory storage for showroom (per session)
let sessionShowroom: ShowroomVehicleItem[] | null = null;
let showroomLoadTimestamp: number | null = null;

// In-memory styleId index cache (preferred over localStorage for reliability)
let sessionStyleIdIndex: Record<string, ShowroomVehicleCardByStyleID> | null = null;

// Used to avoid repeatedly parsing localStorage (expensive) during polling
let attemptedLocalStorageIndexHydration = false;

// Local storage key for styleId index (namespaced by domain to prevent cross-site conflicts in widget deployment)
const getStyleIdIndexKey = (): string => {
  const domain = window.location.hostname.replace(/[^a-zA-Z0-9]/g, '_');
  return `showroom_styleid_index_${domain}`;
};

const normalizeStringArray = (value: unknown): string[] => {
  if (!value) return [];
  const arr = Array.isArray(value) ? value : [value];
  return arr
    .filter((v): v is string => typeof v === 'string' && v.trim().length > 0)
    .map((v) => v.trim());
};

export const loadShowroomForSession = async (): Promise<boolean> => {
  try {
    // Check if already loaded in this session
    if (sessionShowroom && showroomLoadTimestamp && sessionStyleIdIndex) {
      logger.log('✅ Showroom already loaded for this session');
      return true;
    }

    // Fetch showroom from the dealer site (NOT necessarily the current window origin in preview/widget contexts)
    let baseUrl = window.location.origin;
    try {
      const { DealerManager } = await import('./dealerManager');
      const dealer = DealerManager.getCurrentDealer();
      // Only use dealer domain if it looks like a real dealer site (not lovable preview)
      if (dealer?.domain && 
          !dealer.domain.includes('lovable') && 
          !dealer.domain.includes('localhost') &&
          dealer.domain.includes('.')) {
        baseUrl = `https://${dealer.domain.replace(/^https?:\/\//, '')}`;
        logger.log('🏢 Using dealer domain for showroom:', baseUrl);
      } else {
        logger.log('🏢 Using window.origin for showroom (no valid dealer domain):', baseUrl);
      }
    } catch {
      logger.log('🏢 Fallback to window.origin for showroom');
    }

    const showroomUrl = `${baseUrl}/js/json/chatboost/showroom/showroom-index.json`;
    logger.log('🏢 Fetching showroom from:', showroomUrl);

    const response = await fetch(showroomUrl);
    if (!response.ok) {
      console.error('❌ Failed to fetch showroom:', response.status);
      return false;
    }

    // French dealer sites often serve JSON as ISO-8859-1
    // First get the raw text, then try to parse - can't call response.json() after arrayBuffer()
    const buffer = await response.arrayBuffer();
    let showroomData: any;
    try {
      // Try ISO-8859-1 decoding first (common for French sites)
      const decoder = new TextDecoder('iso-8859-1');
      const text = decoder.decode(buffer);
      showroomData = JSON.parse(text);
    } catch {
      // Fallback to UTF-8 decoding
      const decoder = new TextDecoder('utf-8');
      const text = decoder.decode(buffer);
      showroomData = JSON.parse(text);
    }

    // Flatten the nested "available trims" structure
    const flattenedShowroom: ShowroomVehicleItem[] = [];

    if (Array.isArray(showroomData)) {
      for (const modelData of showroomData) {
        const availableTrims = modelData['available trims'] || {};
        
        // Extract each trim as a separate vehicle entry
        for (const trimName in availableTrims) {
          const trimData = availableTrims[trimName];
          
          // Explicitly get styleid from trim data or model data (check all case variations)
          const styleid = trimData.styleId || trimData.styleid || trimData.styleID || trimData.StyleID || 
                         modelData.styleId || modelData.styleid || modelData.styleID || modelData.StyleID;
          
          // Extract critical fields explicitly for clarity
          const finalPrice = trimData['final price'] || trimData.finalPrice || trimData.price || trimData.Price;
          const paymentsUrl = trimData.payments || trimData.Payments;
          const mainPicture = trimData['main picture'] || trimData.mainPicture || trimData.Main_Picture_URL;
          const showroomUrlFr = trimData.Showroom_URL_French;
          const showroomUrlEn = trimData.Showroom_URL_English;
          const bpUrlFr = trimData.BuildAndPrice_URL_French;
          const bpUrlEn = trimData.BuildAndPrice_URL_English;
          
          // Merge parent model data with trim-specific data
          flattenedShowroom.push({
            // Parent level properties
            make: modelData.make,
            model: modelData.model,
            year: modelData.year,
            type: modelData.type,
            condition: modelData.condition,
            'Showroom_Youtube_URL_French': modelData.Showroom_Youtube_URL_French,
            'Showroom_Youtube_URL_English': modelData.Showroom_Youtube_URL_English,
            'Vehicle_Brochure_French': modelData.Vehicle_Brochure_French,
            'Vehicle_Brochure_English': modelData.Vehicle_Brochure_English,
            'inventory count': modelData['inventory count'],
            'model invnetory url french': modelData['model invnetory url french'],
            'model invnetory url english': modelData['model invnetory url english'],
            
            // Trim-specific properties (spread captures everything)
            ...trimData,
            
            // Explicitly set critical fields to ensure they're accessible with consistent naming
            ...(styleid ? { styleid, styleId: styleid } : {}),
            ...(finalPrice ? { 'final price': finalPrice, finalPrice } : {}),
            ...(paymentsUrl ? { payments: paymentsUrl } : {}),
            ...(mainPicture ? { 'main picture': mainPicture, Main_Picture_URL: mainPicture } : {}),
            ...(showroomUrlFr ? { Showroom_URL_French: showroomUrlFr } : {}),
            ...(showroomUrlEn ? { Showroom_URL_English: showroomUrlEn } : {}),
            ...(bpUrlFr ? { BuildAndPrice_URL_French: bpUrlFr } : {}),
            ...(bpUrlEn ? { BuildAndPrice_URL_English: bpUrlEn } : {}),
            
            trim: trimName
          });
        }
      }
    }
    
    // Store flattened data in memory for this session
    sessionShowroom = flattenedShowroom;
    showroomLoadTimestamp = Date.now();
    
    // Create styleId index for fast lookups and keep it in memory for this session
    const styleIdIndex: Record<string, ShowroomVehicleCardByStyleID> = {};
    
    for (const vehicle of flattenedShowroom) {
      const styleId = String(vehicle.styleid || vehicle.styleId || '').trim();
      if (styleId) {
        styleIdIndex[styleId] = {
          styleId: styleId,
          year: vehicle.year || 0,
          make: vehicle.make || '',
          model: vehicle.model || '',
          trim: vehicle.trim || '',
          finalPrice: vehicle['final price'] || vehicle.finalPrice || 0,
          drivetrain: vehicle.drivetrain || vehicle.Drivetrain || '',
          showroomUrlFrench: vehicle.Showroom_URL_French || '',
          showroomUrlEnglish: vehicle.Showroom_URL_English || '',
          buildAndPriceUrlFrench: vehicle.BuildAndPrice_URL_French || '',
          buildAndPriceUrlEnglish: vehicle.BuildAndPrice_URL_English || '',
          mainPicture: vehicle['main picture'] || vehicle.Main_Picture_URL || '',
          showroomYoutubeUrlFrench: normalizeStringArray(vehicle['Showroom_Youtube_URL_French']),
          showroomYoutubeUrlEnglish: normalizeStringArray(vehicle['Showroom_Youtube_URL_English']),
          vehicleBrochureFrench: vehicle['Vehicle_Brochure_French'] || '',
          vehicleBrochureEnglish: vehicle['Vehicle_Brochure_English'] || ''
        };
      }
    }

    const indexedCount = Object.keys(styleIdIndex).length;
    if (indexedCount === 0) {
      console.error('❌ Showroom loaded but produced an empty styleId index');
      return false;
    }

    // Keep index in memory for the whole session (most reliable)
    sessionStyleIdIndex = styleIdIndex;
    attemptedLocalStorageIndexHydration = true;

    // Save index to localStorage as a best-effort cache (can fail on quota)
    try {
      const storageKey = getStyleIdIndexKey();
      localStorage.setItem(storageKey, JSON.stringify(styleIdIndex));
      logger.log(`💾 StyleId index saved to localStorage (${storageKey}): ${indexedCount} vehicles indexed`);
    } catch (error) {
      logger.warn('⚠️ Failed to save styleId index to localStorage (will rely on in-memory cache):', error);
    }

    return true;
  } catch (error) {
    console.error('❌ Error loading showroom:', error);
    return false;
  }
};

export const showroomRetrieval = (criteria: ShowroomFilterCriteria): ShowroomVehicleItem[] => {
  if (!sessionShowroom) {
    logger.warn('⚠️ Showroom not loaded. Call loadShowroomForSession first.');
    return [];
  }

  const filtered = sessionShowroom.filter(vehicle => {
    // Filter by vehicle type (mapped from 'type' field) - handle both singular and plural forms
    if (criteria.vehicleType) {
      const types = Array.isArray(criteria.vehicleType) ? criteria.vehicleType : [criteria.vehicleType];
      const vehicleType = vehicle.type?.toLowerCase();
      
      if (!vehicleType) {
        return false;
      }
      
      // Check if any filter type matches (handling singular/plural)
      const matches = types.some(filterType => {
        const filterLower = filterType.toLowerCase();
        // Exact match
        if (filterLower === vehicleType) return true;
        // Singular form of filter matches plural in data (e.g., 'truck' matches 'trucks')
        if (filterLower + 's' === vehicleType) return true;
        // Plural form of filter matches singular in data (e.g., 'trucks' matches 'truck')
        if (filterLower === vehicleType + 's') return true;
        return false;
      });
      
      if (!matches) {
        return false;
      }
    }

    // Filter by fuel type (mapped from 'fuel' field)
    if (criteria.fuelType) {
      const fuelTypes = Array.isArray(criteria.fuelType) ? criteria.fuelType : [criteria.fuelType];
      if (!vehicle.fuel || !fuelTypes.some(f => f.toLowerCase() === vehicle.fuel?.toLowerCase())) {
        return false;
      }
    }

    // Filter by price range - handle string to number conversion
    const finalPrice = vehicle['final price'] || vehicle['Final price'] || vehicle['Final Price'];
    if (finalPrice !== undefined) {
      // Convert string to number if needed
      const priceValue = typeof finalPrice === 'string' ? parseFloat(finalPrice) : finalPrice;
      
      if (!isNaN(priceValue)) {
        if (criteria.minPrice !== undefined && priceValue < criteria.minPrice) {
          return false;
        }
        if (criteria.maxPrice !== undefined && priceValue > criteria.maxPrice) {
          return false;
        }
      }
    }

    return true;
  });
  
  return filtered;
};

export const getShowroomCount = (): number => {
  return sessionShowroom?.length || 0;
};

export const isShowroomLoaded = (): boolean => {
  return sessionShowroom !== null;
};

export const clearShowroomSession = (): void => {
  sessionShowroom = null;
  showroomLoadTimestamp = null;
  sessionStyleIdIndex = null;
  attemptedLocalStorageIndexHydration = false;
  
  // Also clear the localStorage index
  try {
    const storageKey = getStyleIdIndexKey();
    localStorage.removeItem(storageKey);
    logger.log(`🗑️ Showroom session cleared (including localStorage index: ${storageKey})`);
  } catch (error) {
    console.error('❌ Error clearing localStorage index:', error);
  }
};

/**
 * Attempt to hydrate the in-memory styleId index from localStorage (only once).
 * This avoids repeated JSON.parse during polling and still supports cached reloads.
 */
const hydrateStyleIdIndexFromLocalStorage = (): void => {
  if (sessionStyleIdIndex && Object.keys(sessionStyleIdIndex).length > 0) return;
  if (attemptedLocalStorageIndexHydration) return;

  attemptedLocalStorageIndexHydration = true;

  try {
    const storageKey = getStyleIdIndexKey();
    const indexStr = localStorage.getItem(storageKey);
    if (!indexStr) return;

    const parsed = JSON.parse(indexStr) as Record<string, ShowroomVehicleCardByStyleID>;
    if (parsed && Object.keys(parsed).length > 0) {
      sessionStyleIdIndex = parsed;
    }
  } catch {
    // ignore - we'll rely on fresh fetch in loadShowroomForSession
  }
};

/**
 * Check if the showroom styleId index is available (synchronous)
 */
export const isShowroomIndexReady = (): boolean => {
  hydrateStyleIdIndexFromLocalStorage();
  return !!sessionStyleIdIndex && Object.keys(sessionStyleIdIndex).length > 0;
};

/**
 * Look up a vehicle by its styleid from the localStorage index (synchronous, no retry)
 * Use this version in React components where async operations are not supported
 */
export const getVehicleCardByStyleIdSync = (styleid: string | number): ShowroomVehicleCardByStyleID | null => {
  try {
    hydrateStyleIdIndexFromLocalStorage();

    if (!sessionStyleIdIndex) {
      const storageKey = getStyleIdIndexKey();
      logger.warn(`⚠️ getVehicleCardByStyleIdSync: StyleId index not available (memory/localStorage) (${storageKey}). Showroom may not be loaded yet.`);
      return null;
    }

    const styleIdStr = String(styleid).trim();
    const vehicle = sessionStyleIdIndex[styleIdStr];

    if (!vehicle) {
      logger.warn('⚠️ getVehicleCardByStyleIdSync: Vehicle not found for styleid:', styleid);
      return null;
    }

    return vehicle;
  } catch (error) {
    console.error('❌ getVehicleCardByStyleIdSync: Error looking up styleid from index:', error);
    return null;
  }
};

/**
 * Look up a vehicle by its styleid from the localStorage index (async with retry)
 * Use this version in async parsing layers
 */
export const getVehicleCardByStyleId = async (styleid: string | number, retryAttempt: number = 0): Promise<ShowroomVehicleCardByStyleID | null> => {
  try {
    hydrateStyleIdIndexFromLocalStorage();

    // Prefer in-memory cache when available
    if (sessionStyleIdIndex && Object.keys(sessionStyleIdIndex).length > 0) {
      const styleIdStr = String(styleid).trim();
      const vehicle = sessionStyleIdIndex[styleIdStr] || null;

      if (!vehicle && retryAttempt === 0) {
        logger.log('⏳ getVehicleCardByStyleId: StyleID not found in memory, waiting 2 seconds before retry...');
        await new Promise(resolve => setTimeout(resolve, 2000));
        return getVehicleCardByStyleId(styleid, 1);
      }

      if (!vehicle) {
        logger.warn(`⚠️ getVehicleCardByStyleId: Vehicle not found for styleId: ${styleid} after ${retryAttempt + 1} attempt(s)`);
        logger.log('📋 getVehicleCardByStyleId: Available styleIds (first 10):', Object.keys(sessionStyleIdIndex).slice(0, 10));
        return null;
      }

      return vehicle;
    }

    // Fallback: try localStorage (and hydrate memory from it)
    const storageKey = getStyleIdIndexKey();
    logger.log('🔍 getVehicleCardByStyleId: Looking up styleId:', styleid, `(attempt ${retryAttempt + 1}) in ${storageKey}`);

    const indexStr = localStorage.getItem(storageKey);
    if (!indexStr) {
      logger.warn(`⚠️ getVehicleCardByStyleId: StyleId index not found in localStorage (${storageKey}). Showroom may not be loaded.`);
      return null;
    }

    const index: Record<string, ShowroomVehicleCardByStyleID> = JSON.parse(indexStr);
    sessionStyleIdIndex = index;

    const styleIdStr = String(styleid).trim();
    const vehicle = index[styleIdStr];

    if (!vehicle && retryAttempt === 0) {
      logger.log('⏳ getVehicleCardByStyleId: StyleID not found, waiting 2 seconds before retry...');
      await new Promise(resolve => setTimeout(resolve, 2000));
      return getVehicleCardByStyleId(styleid, 1);
    }

    if (!vehicle) {
      logger.warn(`⚠️ getVehicleCardByStyleId: Vehicle not found for styleId: ${styleid} after ${retryAttempt + 1} attempt(s)`);
      logger.log('📋 getVehicleCardByStyleId: Available styleIds (first 10):', Object.keys(index).slice(0, 10));
      return null;
    }

    return vehicle;
  } catch (error) {
    console.error('❌ getVehicleCardByStyleId: Error looking up styleid from index:', error);
    return null;
  }
};

/**
 * Look up a vehicle by its styleid from the loaded showroom data (full object)
 */
export const getVehicleByStyleId = (styleid: string | number): ShowroomVehicleItem | null => {
  if (!sessionShowroom) {
    logger.warn('⚠️ Showroom not loaded. Cannot lookup styleid:', styleid);
    return null;
  }

  const styleIdStr = String(styleid);
  logger.log('🔍 Looking up styleid:', styleIdStr, 'in', sessionShowroom.length, 'trims');
  
  const vehicle = sessionShowroom.find(v => {
    const vStyleId = String(v.styleid || v.styleID || v.StyleID || '');
    return vStyleId === styleIdStr;
  });
  
  if (!vehicle) {
    logger.warn('⚠️ Vehicle not found for styleid:', styleid);
    logger.log('📋 Available styleids in showroom:', sessionShowroom.map(v => v.styleid || v.styleID || v.StyleID).filter(Boolean));
    return null;
  }

  logger.log('✅ Found vehicle for styleid:', styleid, '→', vehicle.make, vehicle.model, vehicle.trim);
  logger.log('📋 Available fields:', Object.keys(vehicle));
  logger.log('💰 Price fields:', {
    'final price': vehicle['final price'],
    'Final Price': vehicle['Final Price'],
    'Final price': vehicle['Final price'],
    'Price': vehicle['Price'],
    'price': vehicle['price'],
    'MSRP': vehicle['MSRP'],
    'msrp': vehicle['msrp']
  });
  logger.log('🔗 URL fields:', {
    'Showroom_URL_English': vehicle['Showroom_URL_English'],
    'Showroom_URL_French': vehicle['Showroom_URL_French'],
    'model invnetory url english': vehicle['model invnetory url english'],
    'VDP_URL': vehicle['VDP_URL'],
    'vdp_url': vehicle['vdp_url'],
    'url': vehicle['url'],
    'URL': vehicle['URL']
  });
  
  return vehicle;
};
